import { useState } from 'react';
import axios from 'axios';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

const isoToLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export function AdminMoments({ data }) {
  const [moments, setMoments] = useState(null);
  const [editId, setEditId] = useState(null);
  const [deliver, setDeliver] = useState('');
  const [deadline, setDeadline] = useState('');
  const [manual, setManual] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  if (!data) return <div className="text-gray-400">Einen Moment …</div>;
  const { stats } = data;
  const rows = moments || data.moments;

  const STAT_CARDS = [
    { label: 'Momente gesamt', value: stats.total },
    { label: 'Neu (7 Tage)', value: stats.newLast7Days },
    { label: 'Initiatoren', value: stats.uniqueInitiators },
    { label: 'Beiträge gesamt', value: stats.totalContributions },
    { label: 'In Entstehung', value: stats.collecting },
    { label: 'Übergeben', value: stats.released },
    { label: 'Erlebt', value: stats.experienced },
    { label: 'Bewahrt', value: stats.kept },
  ];

  const statusBadge = (m) => {
    if (m.kept) return <span className="px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-400">Bewahrt</span>;
    if (m.experienced) return <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">Erlebt</span>;
    if (m.opened) return <span className="px-2 py-1 rounded text-xs bg-teal-500/20 text-teal-400">Geöffnet</span>;
    if (m.status === 'released') return <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">Übergeben</span>;
    return <span className="px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-400">Entsteht</span>;
  };

  const openEdit = (m) => {
    setEditId(m.id);
    setDeliver(isoToLocal(m.deliverAt));
    setDeadline(isoToLocal(m.deadline));
    setManual(!m.deliverAt);
    setEditError(null);
  };

  const saveSchedule = async (m) => {
    if (saving) return;
    setSaving(true); setEditError(null);
    try {
      const payload = {
        deliverAt: manual || !deliver ? null : new Date(deliver).toISOString(),
        deadline: deadline ? new Date(deadline).toISOString() : null,
      };
      const res = await axios.put(`${API_V2}/admin/moments/${m.id}/schedule`, payload, { withCredentials: true });
      setMoments((rows || data.moments).map((x) => (x.id === m.id ? { ...x, deliverAt: res.data.deliverAt, deadline: res.data.deadline } : x)));
      setEditId(null);
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-moments">
      <div>
        <h2 className="text-lg font-semibold">Momente ({stats.total})</h2>
        <p className="text-sm text-gray-400">Wer erstellt Momente — und wie entwickelt sich das Feature.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-moments-stats">
        {STAT_CARDS.map((s) => (
          <div key={s.label} className="bg-gray-800 rounded-xl p-4">
            <div className="text-2xl font-semibold">{s.value}</div>
            <div className="text-xs text-gray-400 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-gray-800 rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-moments-table">
          <thead className="bg-gray-700">
            <tr>
              <th className="text-left px-4 py-3">Initiator</th>
              <th className="text-left px-4 py-3">Für</th>
              <th className="text-left px-4 py-3">Anlass</th>
              <th className="text-center px-4 py-3">Beiträge</th>
              <th className="text-center px-4 py-3">Eingeladen</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Übergabe</th>
              <th className="text-left px-4 py-3">Erstellt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {rows.map((m) => (
              <>
              <tr key={m.id} className="hover:bg-gray-700/50" data-testid={`admin-moment-${m.id}`}>
                <td className="px-4 py-3">
                  <div>{m.initiatorName}</div>
                  {m.initiatorEmail && <div className="text-xs text-gray-500">{m.initiatorEmail}</div>}
                </td>
                <td className="px-4 py-3 text-gray-300">{m.recipientName}</td>
                <td className="px-4 py-3 text-gray-400">{m.occasion}</td>
                <td className="px-4 py-3 text-center font-medium">{m.contributionCount}</td>
                <td className="px-4 py-3 text-center text-gray-400">{m.participantCount}</td>
                <td className="px-4 py-3 text-center">{statusBadge(m)}</td>
                <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                  <div>{m.deliverAt ? new Date(m.deliverAt).toLocaleString('de-DE', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'manuell'}</div>
                  {m.status !== 'released' && (
                    <button type="button" onClick={() => (editId === m.id ? setEditId(null) : openEdit(m))}
                      data-testid={`admin-moment-edit-${m.id}`}
                      className="text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2 mt-0.5">
                      Datum ändern
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                  {new Date(m.createdAt).toLocaleDateString('de-DE')}
                </td>
              </tr>
              {editId === m.id && (
                <tr key={`${m.id}-edit`} className="bg-gray-700/30" data-testid={`admin-moment-editor-${m.id}`}>
                  <td colSpan={8} className="px-4 py-4">
                    <div className="flex flex-wrap items-end gap-4">
                      {!manual && (
                        <label className="text-xs text-gray-400">
                          Übergabe
                          <input type="datetime-local" value={deliver} onChange={(e) => setDeliver(e.target.value)}
                            data-testid="admin-schedule-deliver-input"
                            className="block mt-1 bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-inherit" />
                        </label>
                      )}
                      <label className="flex items-center gap-2 text-xs text-gray-400 pb-2 cursor-pointer">
                        <input type="checkbox" checked={manual} onChange={(e) => setManual(e.target.checked)} data-testid="admin-schedule-manual-toggle" />
                        Manuelle Freigabe (kein Datum)
                      </label>
                      <label className="text-xs text-gray-400">
                        Beiträge möglich bis (optional)
                        <input type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)}
                          data-testid="admin-schedule-deadline-input"
                          className="block mt-1 bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-inherit" />
                      </label>
                      <button type="button" onClick={() => saveSchedule(m)} disabled={saving || (!manual && !deliver)}
                        data-testid="admin-schedule-save-btn"
                        className="px-4 py-1.5 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 text-sm disabled:opacity-50">
                        {saving ? 'Speichert …' : 'Speichern'}
                      </button>
                      <button type="button" onClick={() => setEditId(null)} data-testid="admin-schedule-cancel-btn"
                        className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200">
                        Abbrechen
                      </button>
                    </div>
                    {editError && <p className="text-xs text-red-400 mt-2" data-testid="admin-schedule-error">{editError}</p>}
                  </td>
                </tr>
              )}
              </>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="text-gray-400 p-4">Noch keine Momente erstellt</div>
        )}
      </div>
    </div>
  );
}
